import { useTranslation } from 'react-i18next'
import { Edit3, Plus, Trash2 } from 'lucide-react'
import { Button, formatDate } from '@asad/crane-ui'

import { DEFAULT_VIEW_ID, type DashboardView } from '../model/dashboardViews'

type DashboardViewsPanelProps = {
  views: DashboardView[]
  activeViewId: string
  allDevicesCount: number
  disabled?: boolean
  onCreate: () => void
  onEdit: (view: DashboardView) => void
  onDelete: (view: DashboardView) => void
}

export const DashboardViewsPanel = ({
  views,
  activeViewId,
  allDevicesCount,
  disabled = false,
  onCreate,
  onEdit,
  onDelete,
}: DashboardViewsPanelProps) => {
  const { t, i18n } = useTranslation()

  return (
    <div className='p-4'>
      <div className='mb-3 flex justify-end'>
        <Button className='gap-2' disabled={disabled} onClick={onCreate}>
          <Plus className='size-4' aria-hidden />
          {t('dashboardViews.newView')}
        </Button>
      </div>
      <div className='flex flex-col gap-2.5'>
        <div className='flex flex-col gap-3 rounded-crane-md bg-crane-card-inset p-3.5 sm:flex-row sm:items-center'>
          <div className='min-w-0 flex-1'>
            <div className='flex items-center gap-2'>
              <span className='truncate font-semibold'>{t('dashboardViews.defaultView')}</span>
              {activeViewId === DEFAULT_VIEW_ID && (
                <span className='rounded-crane-sm bg-crane-primary/15 px-2 py-0.5 text-crane-xs font-semibold text-crane-primary'>
                  {t('dashboardViews.active')}
                </span>
              )}
            </div>
            <div className='mt-1 text-xs text-crane-muted-foreground'>
              {t('dashboardViews.deviceCount', { count: allDevicesCount })}
            </div>
          </div>
        </div>

        {views.map((view) => (
          <div
            key={view.id}
            className='flex flex-col gap-3 rounded-crane-md bg-crane-card-inset p-3.5 sm:flex-row sm:items-center'
          >
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2'>
                <span className='truncate font-semibold'>{view.name}</span>
                {activeViewId === view.id && (
                  <span className='rounded-crane-sm bg-crane-primary/15 px-2 py-0.5 text-crane-xs font-semibold text-crane-primary'>
                    {t('dashboardViews.active')}
                  </span>
                )}
              </div>
              <div className='mt-1 text-xs text-crane-muted-foreground'>
                {t('dashboardViews.deviceCount', { count: view.deviceIds.length })}
                {view.updatedAt
                  ? ` · ${t('dashboardViews.updated', { date: formatDate(view.updatedAt, i18n.language) })}`
                  : ''}
              </div>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Button variant='outline' size='sm' disabled={disabled} onClick={() => onEdit(view)}>
                <Edit3 className='mr-1.5 size-4' aria-hidden />
                {t('commonActions.edit')}
              </Button>
              <Button variant='destructive' size='sm' disabled={disabled} onClick={() => onDelete(view)}>
                <Trash2 className='mr-1.5 size-4' aria-hidden />
                {t('commonActions.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
